import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import { MagnifyingGlass, FilmSlate, Bell, Question } from "@phosphor-icons/react";

const chip = (isActive) =>
  `relative flex h-8 w-8 lg:h-9 lg:w-9 xl:h-10 xl:w-10 items-center justify-center rounded-full border backdrop-blur transition-all duration-200 ${
    isActive
      ? "border-transparent bg-gold text-gold-ink shadow-[0_0_0_3px_rgba(212,175,55,0.18)]"
      : "border-white/10 bg-black/40 text-white hover:-translate-y-0.5 hover:border-gold/50 hover:text-gold hover:shadow-[0_4px_14px_rgba(212,175,55,0.15)]"
  }`;

const ITEMS = [
  { to: "/app/search", icon: MagnifyingGlass, label: "Search", testid: "quick-search" },
  { to: "/app/screening-room", icon: FilmSlate, label: "Discover", testid: "quick-room" },
];

export function QuickIcons({ variant = "float", showAvatar = false, hideScreeningRoom = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { unread, remindersPending } = useNotifications();
  const items = hideScreeningRoom ? ITEMS.filter((it) => it.testid !== "quick-room") : ITEMS;

  return (
    <div
      data-testid={`quick-icons-${variant}`}
      className={variant === "float"
        ? "fixed right-6 top-4 z-[55] hidden items-center gap-2 lg:flex"
        : "flex items-center gap-1 lg:gap-1.5 xl:gap-2"}
    >
      {items.map((it) => (
        <NavLink key={it.to} to={it.to} data-testid={it.testid} title={it.label} className={({ isActive }) => chip(isActive)}>
          <it.icon size={19} />
        </NavLink>
      ))}
      <NavLink to="/app/notifications" data-testid="quick-notifications" title={remindersPending > 0 ? `Alerts — ${remindersPending} reminder${remindersPending > 1 ? "s" : ""} pending` : "Alerts"} className={({ isActive }) => chip(isActive)}>
        <Bell size={19} />
        {unread > 0 && (
          <span data-testid="quick-notifications-badge" className="absolute -right-0.5 -top-0.5 flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-gold px-1 text-[9px] font-extrabold text-gold-ink ring-2 ring-obsidian">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
        {remindersPending > 0 && (
          <span data-testid="quick-notifications-reminder-dot" className="absolute -bottom-0.5 -left-0.5 h-[9px] w-[9px] rounded-full bg-violet-400 ring-2 ring-obsidian" />
        )}
      </NavLink>
      <NavLink to="/app/help" data-testid="quick-help" title="Help" className={({ isActive }) => chip(isActive)}>
        <Question size={19} />
      </NavLink>
      {showAvatar && (
        <button onClick={() => navigate("/app/profile")} data-testid="quick-profile" title="Profile" className="ml-0.5 h-8 w-8 lg:h-8 lg:w-8 xl:h-9 xl:w-9 overflow-hidden rounded-full ring-2 ring-white/15 transition-transform hover:scale-105">
          {user?.picture
            ? <img src={user.picture} alt="" className="h-full w-full object-cover" />
            : <span className="flex h-full w-full items-center justify-center bg-gold/[0.16] font-display text-sm text-gold">{user?.name?.[0]?.toUpperCase()}</span>}
        </button>
      )}
    </div>
  );
}
